import React, { ReactNode } from 'react'
import { Heading, ParkingRates } from '@scentregroup/ui'
import sortBy from 'lodash/sortBy'
import max from 'lodash/max'
import omit from 'lodash/omit'
import { parkingZones } from '../parking'
import Markdown from '../markdown'
import {
  ParkingModule,
  CFCarParkGroup,
  CFParkingRules,
} from '../../../types/contentful'
import { Centre } from '../../../types/contentful-v1'
import { RowEntry, MetaRowEntry, ExpandTitle } from '../info-accordion'
import Trackable from '../trackable'
import { trackingActions, trackingTypes } from '../../constants'

const MINS_IN_HOUR = 60
const EFFECTIVELY_INFINITY = 100000

function bodyFromParkingRules(parkingModule: CFParkingRules): JSX.Element {
  const rateCards = parkingModule.ratesCardsCollection.items.map(rateCard => {
    const unsortedRates = rateCard.ratesCollection.items.map(rate => ({
      to: rate.toInMinutes / MINS_IN_HOUR,
      ...rate,
    }))
    const rates = sortBy(unsortedRates, rate => rate.to || EFFECTIVELY_INFINITY)
    const maximumRate = max(rates.map(rate => rate.rate))
    return { ...omit(rateCard, 'ratesCollection'), rates, maximumRate }
  })
  const parkingRateProps = {
    ...omit(parkingModule, 'ratesCardsCollection'),
    rateCards,
  }
  const cinemaValidation = parkingRateProps.cinemaValidation ? (
    <Markdown content={parkingRateProps.cinemaValidation} />
  ) : null
  return (
    <ParkingRates
      {...parkingRateProps}
      cinemaValidation={cinemaValidation}
      heading=""
    />
  )
}
function rowsFromCarParkGroup(
  parkingModule: CFCarParkGroup,
  centre: Centre
): RowEntry[] {
  const carParks = parkingModule.carParksCollection.items
  const parkZones = parkingZones({ carParks, centre, noBorder: true })
  return parkZones.map((content: JSX.Element) => ({ content }))
}

export default function accordionEntriesFromModules(
  modules: ParkingModule[],
  centre: Centre
): MetaRowEntry[] {
  return modules.map(parkingModule => {
    const { title, __typename } = parkingModule
    const heading = <Heading type="medium">{title || __typename}</Heading>
    const expandTitle: ExpandTitle = { closed: heading, opened: heading }
    if ('carParksCollection' in parkingModule) {
      const rows = rowsFromCarParkGroup(parkingModule, centre)
      return { expandTitle, rows }
    }
    let body: ReactNode = ''
    if ('ratesCardsCollection' in parkingModule) {
      body = bodyFromParkingRules(parkingModule)
    } else if ('bodyCopy' in parkingModule) {
      body = <Markdown content={parkingModule.bodyCopy} />
    }
    body = (
      <Trackable
        {...parkingModule.analyticsMetadata}
        trackingAction={trackingActions.viewPromotion}
        type={trackingTypes.promotion}
      >
        {body}
      </Trackable>
    )
    const rows = [{ content: body }]
    return { expandTitle, rows }
  })
}
